<template>
  <div>
    <b-sidebar v-model="show" right shadow backdrop bg-variant="light" no-header width="70vw">
      <template>
        <div class="py-3 border-bottom px-3">
          <b-row align-v="center" class="mb-2">
            <div class="col-auto">
              <b-button variant="outline-secondary" size="sm" @click="show = false">
                <i class="fa fa-times"></i> Close
              </b-button>
            </div>
            <div class="col">
              <h5 class="mb-0">Client Billing Ledger</h5>
            </div>
          <div class="col-auto d-flex flex-wrap" style="gap: 6px;">
            <span v-if="relatedType" class="badge" :class="relatedTypeBadgeClass">{{ relatedTypeLabel }}</span>
            <span v-if="headerBrandName" class="badge badge-secondary">{{ headerBrandName }}</span>
            <span v-if="headerWarehouseName" class="badge badge-info"><i class="fa fa-home mr-1"></i>{{ headerWarehouseName }}</span>
            <span v-if="referenceNum" class="badge badge-light border"><i class="fa fa-hashtag mr-1"></i>{{ referenceNum }}</span>
          </div>
          </b-row>
        </div>
        <div class="p-3">
            <b-card class="border client-billing-ledger-related-charges" no-body>
              <div v-if="loading" class="text-center py-5">
                <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
              </div>

              <div v-else-if="!items.length" class="text-center py-5 text-muted">
                No billing entries found.
              </div>

              <div v-else class="table-listing">
                <b-table
                  :items="items"
                  :fields="fields"
                  head-variant="dark"
                  responsive
                  small
                  class="mb-0"
                >
                  <template #cell(brandId)="data">
                    <span v-if="myBrands && myBrands.find(function(a) { return a.id == data.item.brandId; })">
                      {{ myBrands.find(function(a) { return a.id == data.item.brandId; }).name }}
                    </span>
                    <span v-else>N / A</span>
                  </template>

                  <template #cell(processedOn)="data">
                    <span v-if="data.item.processedOn && !data.item.processedOn.endsWith('Z')">
                      {{ (data.item.processedOn + 'Z') | moment('MMM Do YY, h:mm a') }}
                    </span>
                    <span v-else-if="data.item.processedOn">
                      {{ data.item.processedOn | moment('MMM Do YY, h:mm a') }}
                    </span>
                  </template>

                  <template #cell(type)="data">
                    <span class="badge badge-warning" v-if="data.item.type == 'StorefrontOrder'">Storefront Order</span>
                    <span class="badge badge-info" v-else-if="data.item.type == 'SmallParcelShipment'">Small Parcel</span>
                    <span class="badge badge-primary" v-else-if="data.item.type == 'BulkShipment'">Bulk Shipment</span>
                    <span class="badge badge-primary" v-else-if="data.item.type == 'WarehouseShipment'">WH Shipment</span>
                    <span class="badge badge-primary" v-else-if="data.item.type == 'WorkOrder'">Work Order</span>
                    <span class="badge badge-primary" v-else-if="data.item.type == 'OrderReturn'">Order Return</span>
                    <span class="badge badge-primary" v-else-if="data.item.type == 'DirectInventoryUpdate'">Adjustment</span>
                    <span class="badge badge-secondary" v-else>Unknown</span>
                  </template>

                  <template #cell(orderNumber)="data">
                    <div v-b-tooltip.hover.left="'Carrier: [' + data.item.carrier + '] Service: [' + data.item.service + ']'">
                      <span class="badge badge-warning" v-if="data.item.type == 'StorefrontOrder'">
                        <router-link :to="{ name: 'Common.OrderDetails', query: { id: data.item.orderId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-info" v-else-if="data.item.type == 'OrderReturn'">
                        <router-link :to="{ name: 'Storefronts.OrderReturns.Edit', params: { orderReturnId: data.item.orderReturnId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-info" v-else-if="data.item.type == 'SmallParcelShipment'">
                        <router-link :to="{ name: 'Warehouses.ManualShipments.Edit', params: { id: data.item.manualShipmentId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-primary" v-else-if="data.item.type == 'BulkShipment'">
                        <router-link class="text-white" :to="{ name: 'Warehouses.BulkShipments.View', params: { id: data.item.manualShipmentId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-info" v-else-if="data.item.type == 'WarehouseShipment'">
                        <router-link :to="{ name: 'Warehouses.WarehouseTransfers.View', params: { id: data.item.warehouseShipmentId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-info" v-else-if="data.item.type == 'WorkOrder'">
                        <router-link :to="{ name: 'Manufacturing.WorkOrder.Edit', params: { id: data.item.workOrderId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-info" v-else-if="data.item.type == 'DirectInventoryUpdate'">
                        <router-link :to="{ name: 'Inventory.DIU.View', params: { id: data.item.directInventoryUpdateId } }">{{ data.item.orderNumber }}</router-link>
                      </span>
                      <span class="badge badge-secondary" v-else>{{ data.item.orderNumber }}</span>
                    </div>
                  </template>

                  <template #cell(chargeType)="data">
                    <BillingChargeTypeBadge :chargeType="data.item.chargeType" />
                  </template>

                  <template #cell(chargeLabel)="data">
                    <div class="d-inline-block small mr-1 px-1 rounded bg-primary" v-b-tooltip.hover.top="'Tracking: ' + data.item.trackingNumber" v-if="data.item.trackingNumber"><i class="fa fa-dropbox"></i></div>
                    <div class="d-inline-block small mr-1 px-1 rounded bg-info" v-b-tooltip.hover.top="'Unit: ' + data.item.unitLabel" v-if="data.item.unitLabel && data.item.unitLabel != '-'"><i class="fa fa-tag"></i></div>
                    <span v-if="!data.item.zoneId">{{ data.item.chargeLabel || 'NONE' }}</span>
                    <u v-else v-b-tooltip.hover.bottom="'Zone: [' + data.item.zoneId + '] Weight: [' + data.item.weight + ']'">{{ data.item.chargeLabel || 'NONE' }}</u>
                  </template>

                  <template #cell(singleUnitCharge)="data">
                    <BillingChargeAmount :charge-amount="data.item.singleUnitCharge" :charge-currency="data.item.currency" />
                  </template>

                  <template #cell(lineItemTotal)="data">
                    <span v-if="data.item.comment" v-b-tooltip.hover.bottom="data.item.comment" class="badge" :class="{ 'badge-info': data.item.rateCardItemId, 'badge-danger': data.item.needsReview, 'badge-warning': (!data.item.rateCardItemId && !data.item.needsReview) }">
                      <BillingChargeAmount :charge-amount="data.item.lineItemTotal" :charge-currency="data.item.currency" />
                    </span>
                    <span v-else>
                      <BillingChargeAmount :charge-amount="data.item.lineItemTotal" :charge-currency="data.item.currency" />
                    </span>
                  </template>

                  <template #cell(id)="data">
                    <BillingEntryActions
                      :entry="data.item"
                      :is-developer="is('developer')"
                      @changed="loadItems"
                    />
                  </template>
                </b-table>

                <b-pagination
                  v-if="totalRows > perPage"
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  class="mt-2 px-3"
                  size="sm"
                  @change="onPageChange"
                />
              </div>
              <b-card-footer class="bg-light">
                <b-alert v-if="hasInvoicedCharges" variant="warning" show class="mb-3">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  <strong>Some charges were already invoiced!</strong> Any new charges you add will show up on next week's invoice instead, referencing this transaction.
                </b-alert>
                <h6 class="mb-2 pb-2 border-bottom font-weight-bold">Add Manual Charge</h6>
                <b-row>
                  <b-col cols="12" md="3">
                    <b-form-group label="Charge Type:" class="mb-2">
                      <b-form-select v-model="manualChargeForm.chargeType" :options="manualChargeTypeOptions" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="3">
                    <b-form-group label="Charge Label:" class="mb-2">
                      <b-form-input v-model="manualChargeForm.chargeLabel" placeholder="This will appear on the invoice summary." />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="1">
                    <b-form-group label="Units:" class="mb-2">
                      <b-form-input v-model="manualChargeForm.numberOfUnits" type="number" step="0.01" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="2">
                    <b-form-group label="Per Unit:" class="mb-2">
                      <b-input-group prepend="$">
                        <b-form-input v-model="manualChargeForm.singleUnitCharge" type="number" step="0.01" />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="1">
                    <b-form-group label="Total:" class="mb-2">
                      <b-input-group prepend="$">
                        <b-form-input class="border" :value="manualChargeTotal" readonly />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="2" class="d-flex align-items-end pb-2">
                    <b-button variant="primary" block :disabled="savingManualCharge" @click="SubmitManualCharge">
                      <i class="fa fa-spinner fa-spin" v-if="savingManualCharge"></i>
                      <i class="fa fa-plus" v-else></i> 
                      Add New Charge
                    </b-button>
                  </b-col>
                </b-row>
              </b-card-footer>
            </b-card>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import BillingChargeTypeBadge from './BillingChargeTypeBadge.vue';
import BillingChargeAmount from './BillingChargeAmount.vue';
import BillingEntryActions from './BillingEntryActions.vue';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'ClientBillingLedgerSidebar',
  mixins: [myCommonDataMixin],
  components: {
    BillingChargeTypeBadge,
    BillingChargeAmount,
    BillingEntryActions
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    relatedId: {
      type: String,
      default: null
    },
    relatedType: {
      type: String,
      default: null
    },
    invoiceId: {
      type: String,
      default: null
    },
    brandId: {
      type: String,
      default: null
    },
    warehouseId: {
      type: String,
      default: null
    },
    referenceNum: {
      type: String,
      default: null
    }
  },
  data: function() {
    return {
      items: [],
      loading: false,
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      savingManualCharge: false,
      manualChargeTypeOptions: [
        { value: 'ConnectAdministrative', text: 'Administrative Charge' },
        { value: 'ConnectFulfillmentCharge', text: 'Fulfillment Charge' }
      ],
      manualChargeForm: {
        invoiceId: null,
        brandId: null,
        warehouseId: null,
        chargeType: 'ConnectFulfillmentCharge',
        chargeLabel: 'Other',
        refNum: '',
        numberOfUnits: 1,
        singleUnitCharge: 0
      },
      fields: [
        //{ key: 'brandId', label: 'Client', sortable: false },
        //{ key: 'type', label: 'Type', sortable: false },
        //{ key: 'orderNumber', label: 'Ref #', sortable: false },
        { key: 'processedOn', label: 'Charged At', sortable: false },
        { key: 'trackingNumber', label: 'Tracking #', sortable: false },
        { key: 'chargeType', label: 'Charge Type', sortable: false },
        { key: 'chargeLabel', label: 'Label', sortable: false },
        { key: 'unitLabel', label: 'Unit Label', sortable: false },
        { key: 'singleUnitCharge', label: 'Per Unit', sortable: false },
        { key: 'numberOfUnits', label: 'Units', sortable: false },
        { key: 'lineItemTotal', label: 'Total', sortable: false },
        { key: 'id', label: 'Actions', sortable: false }
      ]
    };
  },
  computed: {
    show: {
      get: function() { return this.value; },
      set: function(val) { this.$emit('input', val); }
    },
    hasInvoicedCharges: function() {
      return this.items.some(function(a) { return !!a.brandInvoiceId; });
    },
    manualChargeTotal: function() {
      var units = parseFloat(this.manualChargeForm.numberOfUnits) || 0;
      var perUnit = parseFloat(this.manualChargeForm.singleUnitCharge) || 0;
      return (units * perUnit).toFixed(2);
    },
    headerBrandName: function() {
      if (!this.brandId || !this.myBrands) return null;
      var brand = this.myBrands.find(function(a) { return a.id == this.brandId; }.bind(this));
      return brand ? brand.name : this.brandId;
    },
    headerWarehouseName: function() {
      if (!this.warehouseId || !this.warehousesListAll) return null;
      var wh = this.warehousesListAll.find(function(a) { return a.value == this.warehouseId; }.bind(this));
      return wh ? wh.text : this.warehouseId;
    },
    relatedTypeLabel: function() {
      var labels = {
        StorefrontOrder: 'Storefront Order',
        WorkOrder: 'Work Order',
        OrderReturn: 'Order Return',
        BulkShipment: 'Bulk Shipment',
        SmallParcelShipment: 'Small Parcel',
        DirectInventoryUpdate: 'Adjustment',
        WarehouseShipment: 'WH Shipment'
      };
      return labels[this.relatedType] || this.relatedType;
    },
    relatedTypeBadgeClass: function() {
      var classes = {
        StorefrontOrder: 'badge-warning',
        WorkOrder: 'badge-primary',
        OrderReturn: 'badge-info',
        BulkShipment: 'badge-primary',
        SmallParcelShipment: 'badge-info',
        DirectInventoryUpdate: 'badge-primary',
        WarehouseShipment: 'badge-info'
      };
      return classes[this.relatedType] || 'badge-secondary';
    }
  },
  watch: {
    value: function(val) {
      if (val && this.relatedId) {
        this.currentPage = 1;
        this.loadItems();
        this.ResetManualChargeForm();
      }
    }
  },
  methods: {
    buildFilter: function() {
      var filter = {};
      if (this.relatedType === 'StorefrontOrder') filter.orderId = this.relatedId;
      else if (this.relatedType === 'WorkOrder') filter.workOrderId = this.relatedId;
      else if (this.relatedType === 'OrderReturn') filter.orderReturnId = this.relatedId;
      else if (this.relatedType === 'BulkShipment') filter.manualShipmentId = this.relatedId;
      else if (this.relatedType === 'SmallParcelShipment') filter.manualShipmentId = this.relatedId;
      else if (this.relatedType === 'DirectInventoryUpdate') filter.directInventoryUpdateId = this.relatedId;
      else if (this.relatedType === 'WarehouseShipment') filter.warehouseShipmentId = this.relatedId;
      return filter;
    },
    loadItems: function() {
      this.loading = true;
      this.$http.post('/api/common/orderBillingEntries/listItems', Object.assign({}, this.buildFilter(), {
        page: this.currentPage,
        perPage: this.perPage,
        sortDesc: false
      }))
        .then(function(resp) {
          this.items = (resp.data && resp.data.items) || [];
          this.totalRows = (resp.data && resp.data.total) || 0;
          this.loading = false;
        }.bind(this))
        .catch(function() {
          this.loading = false;
        }.bind(this));
    },
    onPageChange: function(page) {
      this.currentPage = page;
      this.loadItems();
    },
    ResetManualChargeForm: function() {
      this.manualChargeForm = {
        invoiceId: this.invoiceId || null,
        brandId: this.brandId || null,
        warehouseId: this.warehouseId || null,
        chargeType: 'ConnectFulfillmentCharge',
        chargeLabel: 'Other',
        refNum: this.referenceNum || null,
        numberOfUnits: 1,
        singleUnitCharge: 0
      };
    },
    SubmitManualCharge: function() {
      if (!this.manualChargeForm.chargeLabel) {
        this.$swal('Error', 'Charge Label is required.', 'error');
        return;
      }
      var payload = Object.assign({}, this.manualChargeForm);
      if (this.relatedType === 'StorefrontOrder') payload.orderId = this.relatedId;
      else if (this.relatedType === 'OrderReturn') payload.orderReturnId = this.relatedId;
      else if (this.relatedType === 'BulkShipment') payload.manualShipmentId = this.relatedId;
      else if (this.relatedType === 'SmallParcelShipment') payload.manualShipmentId = this.relatedId;
      else if (this.relatedType === 'DirectInventoryUpdate') payload.directInventoryUpdateId = this.relatedId;
      else if (this.relatedType === 'WarehouseShipment') payload.warehouseShipmentId = this.relatedId;
      this.savingManualCharge = true;
      this.$http.post('/api/common/orderBillingEntries/createManualCharge', payload)
        .then(function() {
          this.savingManualCharge = false;
          this.$swal({ title: 'Charge added!', icon: 'success' });
          this.ResetManualChargeForm();
          this.loadItems();
        }.bind(this))
        .catch(function(err) {
          this.savingManualCharge = false;
          var msg = (err.response && err.response.data && err.response.data.errorMessage) || 'Failed to save charge.';
          this.$swal({ title: msg, icon: 'error' });
        }.bind(this));
    }
  }
};
</script>
